"use client";

import { useState, useEffect, useCallback, useTransition } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ThumbsUp, MessageCircle, Send, Pencil, Trash2 } from "lucide-react";
import {
  getComments,
  postComment,
  postReply,
  toggleLike,
  editComment,
  deleteComment,
  type CommentDTO,
} from "@/lib/actions/comments";

const AVATAR_COLORS = ["#1877F2", "#7c3aed", "#0891b2", "#059669", "#d97706"];
function avatarFor(name: string) {
  const letter = (name.trim()[0] ?? "?").toUpperCase();
  return { letter, color: AVATAR_COLORS[letter.charCodeAt(0) % AVATAR_COLORS.length] };
}

function timeAgo(iso: string) {
  const then = new Date(iso).getTime();
  const secs = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function CommunityComments({ pageId, blurb }: { pageId: string; blurb?: string }) {
  const pathname = usePathname();
  const nextParam = encodeURIComponent(`${pathname}#comments`);

  const [comments, setComments] = useState<CommentDTO[]>([]);
  const [currentUser, setCurrentUser] = useState<{ id: string; name: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [, startTransition] = useTransition();

  const [draft, setDraft] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const refresh = useCallback(async () => {
    const data = await getComments(pageId);
    setComments(data.comments);
    setCurrentUser(data.currentUser);
    setLoading(false);
  }, [pageId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handlePost = () => {
    if (!draft.trim()) return;
    if (!currentUser) {
      setShowAuthPrompt(true);
      return;
    }
    const body = draft.trim();
    setDraft("");
    setShowAuthPrompt(false);
    startTransition(async () => {
      await postComment(pageId, body);
      await refresh();
    });
  };

  const handleReply = (commentId: string) => {
    if (!replyDraft.trim()) return;
    if (!currentUser) {
      setShowAuthPrompt(true);
      return;
    }
    const body = replyDraft.trim();
    setReplyDraft("");
    setReplyingTo(null);
    startTransition(async () => {
      await postReply(commentId, body);
      await refresh();
    });
  };

  const handleLike = (commentId: string) => {
    if (!currentUser) {
      setShowAuthPrompt(true);
      return;
    }
    // Optimistic toggle, then reconcile with the server.
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? { ...c, likedByMe: !c.likedByMe, likes: c.likes + (c.likedByMe ? -1 : 1) }
          : c,
      ),
    );
    startTransition(async () => {
      await toggleLike(commentId);
      await refresh();
    });
  };

  const startEdit = (id: string, body: string) => {
    setReplyingTo(null);
    setEditingId(id);
    setEditDraft(body);
  };

  const saveEdit = (id: string) => {
    const body = editDraft.trim();
    if (!body) return;
    setEditingId(null);
    startTransition(async () => {
      await editComment(id, body);
      await refresh();
    });
  };

  const handleDelete = (id: string) => {
    if (!window.confirm("Delete this comment? This can't be undone.")) return;
    if (editingId === id) setEditingId(null);
    startTransition(async () => {
      await deleteComment(id);
      await refresh();
    });
  };

  return (
    <section id="comments" className="scroll-mt-24">
      {/* Header */}
      <div className="mb-4 flex items-center gap-2.5">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">What players are saying</h2>
        <span
          className="rounded-full px-2.5 py-0.5 text-xs font-bold"
          style={{ background: "#eff6ff", color: "#1877F2" }}
        >
          {comments.length}
        </span>
      </div>

      {/* Compose box */}
      <div
        className="mb-6 overflow-hidden rounded-2xl bg-white"
        style={{ border: "1.5px solid #dde1e7", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
      >
        <div className="px-5 pt-4 pb-3">
          <p className="mb-2.5 text-sm font-semibold text-[#0f172a]">
            {blurb ?? "Confirm a link worked or share a tip."}
          </p>
          <textarea
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setShowAuthPrompt(false); }}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handlePost(); }}
            placeholder="e.g. FLORALLAKES still works as of 10am…"
            rows={3}
            className="w-full resize-none rounded-xl bg-[#f8fafc] px-3 py-2.5 text-base leading-relaxed text-[#374151] outline-none transition placeholder:text-[#9ca3af] focus:bg-white"
            style={{ border: "1.5px solid #dde1e7" }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "#1877F2")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "#dde1e7")}
          />
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-[#9ca3af]">
              {currentUser ? `Posting as ${currentUser.name}` : "Cmd+Enter to post"}
            </p>
            <button
              type="button"
              onClick={handlePost}
              disabled={!draft.trim()}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-40"
              style={{ background: "#1877F2" }}
            >
              <Send className="h-3.5 w-3.5" aria-hidden />
              Post
            </button>
          </div>
        </div>

        {/* Auth prompt — slides in when a signed-out visitor tries to post */}
        {showAuthPrompt && (
          <div
            className="flex flex-col items-start gap-3 border-t border-[#f1f5f9] px-5 py-4 sm:flex-row sm:items-center"
            style={{ background: "#fffbeb" }}
          >
            <p className="flex-1 text-sm text-[#6b7280]">
              <span className="font-semibold text-[#0f172a]">Almost there.</span> Sign in to post your comment.
            </p>
            <div className="flex shrink-0 gap-2">
              <Link
                href={`/login?next=${nextParam}`}
                className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold text-white transition hover:opacity-90"
                style={{ background: "#1877F2" }}
              >
                Sign in
              </Link>
              <Link
                href={`/signup?next=${nextParam}`}
                className="flex items-center rounded-xl px-4 py-2 text-sm font-bold text-[#374151] transition hover:bg-[#f1f5f9]"
                style={{ border: "1.5px solid #dde1e7" }}
              >
                Sign up
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Comment feed. While the initial fetch is in flight we render nothing
          (rather than a "Loading comments…" box) so the page never shows a
          skeleton/loading flash. The feed just appears once data arrives. */}
      {loading ? null : comments.length === 0 ? (
        <div
          className="flex flex-col items-center rounded-2xl bg-white px-5 py-10 text-center"
          style={{ border: "1.5px solid #dde1e7", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
        >
          <MessageCircle className="mb-3 h-8 w-8 text-[#cbd5e1]" aria-hidden />
          <p className="text-base font-semibold text-[#0f172a]">No comments yet</p>
          <p className="mt-1 text-sm text-[#64748b]">
            Be the first to let other players know if a code worked for you.
          </p>
        </div>
      ) : (
      <ul
        className="overflow-hidden rounded-2xl bg-white divide-y divide-[#f1f5f9]"
        style={{ border: "1.5px solid #dde1e7", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
      >
        {comments.map((c) => {
          const a = avatarFor(c.author);
          return (
          <li
            key={c.id}
            className="px-5 py-4"
          >
            <div className="flex gap-3">
              {/* Avatar */}
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base font-extrabold text-white"
                style={{ background: a.color }}
              >
                {a.letter}
              </div>

              <div className="min-w-0 flex-1">
                {/* Meta */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-base font-bold text-[#0f172a]">{c.author}</span>
                  <span className="text-xs text-[#9ca3af]">{timeAgo(c.createdAt)}</span>
                </div>

                {/* Body (or inline editor) */}
                {editingId === c.id ? (
                  <div className="mt-1.5">
                    <textarea
                      autoFocus
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveEdit(c.id);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      rows={3}
                      className="w-full resize-none rounded-xl bg-[#f8fafc] px-3 py-2.5 text-base leading-relaxed text-[#374151] outline-none transition focus:bg-white"
                      style={{ border: "1.5px solid #dde1e7" }}
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => saveEdit(c.id)}
                        disabled={!editDraft.trim()}
                        className="rounded-xl px-3 py-1.5 text-xs font-bold text-white transition hover:opacity-90 disabled:opacity-40"
                        style={{ background: "#1877F2" }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="rounded-xl px-3 py-1.5 text-xs font-bold text-[#64748b] transition hover:bg-[#f1f5f9]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1.5 text-base leading-relaxed text-[#374151] whitespace-pre-wrap break-words">{c.body}</p>
                )}

                {/* Actions */}
                <div className="mt-2.5 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleLike(c.id)}
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition hover:bg-[#f1f5f9] active:scale-[0.97]"
                    style={{ color: c.likedByMe ? "#1a9e5c" : "#64748b" }}
                  >
                    <ThumbsUp className="h-3.5 w-3.5" aria-hidden />
                    {c.likes}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReplyingTo(replyingTo === c.id ? null : c.id)}
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition hover:bg-[#f1f5f9] active:scale-[0.97]"
                    style={{ color: replyingTo === c.id ? "#1877F2" : "#64748b" }}
                  >
                    <MessageCircle className="h-3.5 w-3.5" aria-hidden />
                    Reply{c.replies.length > 0 && ` (${c.replies.length})`}
                  </button>
                  {c.mine && (
                    <>
                      <button
                        type="button"
                        onClick={() => startEdit(c.id, c.body)}
                        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-[#64748b] transition hover:bg-[#f1f5f9] active:scale-[0.97]"
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(c.id)}
                        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-[#ef4444] transition hover:bg-[#fef2f2] active:scale-[0.97]"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                        Delete
                      </button>
                    </>
                  )}
                </div>

                {/* Replies */}
                {c.replies.length > 0 && (
                  <ul className="mt-3 space-y-2.5 border-l-2 border-[#e2e8f0] pl-4">
                    {c.replies.map((r) => {
                      const ra = avatarFor(r.author);
                      return (
                      <li key={r.id} className="flex gap-2.5">
                        <div
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-extrabold text-white"
                          style={{ background: ra.color }}
                        >
                          {ra.letter}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-[#0f172a]">{r.author}</span>
                            <span className="text-xs text-[#9ca3af]">{timeAgo(r.createdAt)}</span>
                          </div>
                          {editingId === r.id ? (
                            <div className="mt-1">
                              <input
                                autoFocus
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveEdit(r.id);
                                  if (e.key === "Escape") setEditingId(null);
                                }}
                                className="w-full rounded-xl bg-[#f8fafc] px-3 py-2 text-base outline-none transition focus:bg-white"
                                style={{ border: "1.5px solid #dde1e7" }}
                              />
                              <div className="mt-1.5 flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => saveEdit(r.id)}
                                  disabled={!editDraft.trim()}
                                  className="rounded-lg px-2.5 py-1 text-xs font-bold text-white transition hover:opacity-90 disabled:opacity-40"
                                  style={{ background: "#1877F2" }}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingId(null)}
                                  className="rounded-lg px-2.5 py-1 text-xs font-bold text-[#64748b] transition hover:bg-[#f1f5f9]"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="mt-0.5 text-base leading-relaxed text-[#374151] whitespace-pre-wrap break-words">{r.body}</p>
                              {r.mine && (
                                <div className="mt-1 flex items-center gap-3">
                                  <button
                                    type="button"
                                    onClick={() => startEdit(r.id, r.body)}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#64748b] transition hover:text-[#1877F2]"
                                  >
                                    <Pencil className="h-3 w-3" aria-hidden />
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(r.id)}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#ef4444] transition hover:opacity-80"
                                  >
                                    <Trash2 className="h-3 w-3" aria-hidden />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </li>
                      );
                    })}
                  </ul>
                )}

                {/* Reply input */}
                {replyingTo === c.id && (
                  <div className="mt-3 flex gap-2">
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-extrabold text-white"
                      style={{ background: "#1877F2" }}
                    >
                      {currentUser ? avatarFor(currentUser.name).letter : "?"}
                    </div>
                    <div className="flex flex-1 gap-2">
                      <input
                        autoFocus
                        value={replyDraft}
                        onChange={(e) => setReplyDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleReply(c.id);
                          if (e.key === "Escape") setReplyingTo(null);
                        }}
                        placeholder={`Reply to ${c.author}...`}
                        className="flex-1 rounded-xl bg-[#f8fafc] px-3 py-2 text-base outline-none transition focus:bg-white"
                        style={{ border: "1.5px solid #dde1e7" }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = "#1877F2")}
                        onBlur={(e) => (e.currentTarget.style.borderColor = "#dde1e7")}
                      />
                      <button
                        type="button"
                        onClick={() => handleReply(c.id)}
                        disabled={!replyDraft.trim()}
                        className="rounded-xl px-3 py-2 text-white transition disabled:opacity-40 active:scale-[0.97]"
                        style={{ background: "#1877F2" }}
                      >
                        <Send className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </li>
          );
        })}
      </ul>
      )}
    </section>
  );
}
